import type { IAuthClient } from '@micro-frontend-react/employee-experience/lib/IAuthClient';
import type { SagaIterator } from 'redux-saga';
import { all, call, getContext, put, takeLatest } from 'redux-saga/effects';

import { PBTApiEndpoints } from './PBTConstants';
import { FeatureTreeActionType } from '../Redux/FeatureTree/action-types';
import type { IFeatureTree, IRequestFeatureTreeAction } from '../Redux/FeatureTree/action-types';
import { receiveFeatureTree, featureTreeError } from '../Redux/FeatureTree/actions';

function auth(authClient: IAuthClient) {
  return Promise.resolve(authClient.acquireToken(__SCOPES__));
}

/** Error carrying the HTTP status + server message of a failed feature-config call. */
class FeatureTreeApiError extends Error {
  constructor(public readonly status: number, public readonly serverMessage: string) {
    super(`Feature-config API returned ${status}`);
    this.name = 'FeatureTreeApiError';
    Object.setPrototypeOf(this, FeatureTreeApiError.prototype);
  }
}

/** True when a 403 is the app-wide geo-restriction block (distinctive "location" message). */
function isGeoBlock(error: unknown): boolean {
  return (
    error instanceof FeatureTreeApiError &&
    error.status === 403 &&
    /location/i.test(error.serverMessage ?? '')
  );
}

/** True when a 403 is the app-wide role block: user has no authorized PBT security group. */
function isNoRoleBlock(error: unknown): boolean {
  return (
    error instanceof FeatureTreeApiError &&
    error.status === 403 &&
    /security group/i.test(error.serverMessage ?? '')
  );
}

/** POSTs the user's Entra security group IDs (GUIDs); backend returns role/country + nested feature tree. */
async function resolveTreeApi(token: string, groups: string[], timeoutMs = 15000): Promise<IFeatureTree> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(PBTApiEndpoints.SecurityFeatureConfig, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ groups }),
      signal: controller.signal,
    });
    if (!response.ok) {
      // Read the body as text first so the server's reason is captured regardless of its shape
      // (PascalCase JSON, camelCase JSON, a bare JSON string, or a plain-text body). Both app-wide
      // gates (geo + role) return their message here, and the saga routes the user on it.
      let bodyMessage = '';
      const rawBody = await response.text().catch(() => '');
      if (rawBody) {
        try {
          const errJson = JSON.parse(rawBody);
          bodyMessage = (
            errJson?.error?.message ??
            errJson?.Error?.Message ??
            errJson?.message ??
            errJson?.Message ??
            (typeof errJson === 'string' ? errJson : '')
          ) as string;
        } catch {
          // Non-JSON error body — use the raw text as the message.
          bodyMessage = rawBody;
        }
      }
      throw new FeatureTreeApiError(response.status, bodyMessage);
    }
    const json = await response.json();
    return (json?.data ?? json?.Data) as IFeatureTree;
  } finally {
    clearTimeout(timeoutId);
  }
}

export function* requestFeatureTreeSagas(): SagaIterator {
  yield all([takeLatest(FeatureTreeActionType.REQUEST_FEATURE_TREE, fetchFeatureTree)]);
}

function* fetchFeatureTree(request: IRequestFeatureTreeAction): SagaIterator {
  const authClient: IAuthClient = yield getContext('authClient');
  try {
    const token: string = yield call(auth, authClient);
    const tree: IFeatureTree = yield call(resolveTreeApi, token, request.payload.groups);
    yield put(receiveFeatureTree(tree));
  } catch (ex) {
    if (isGeoBlock(ex)) {
      yield put(featureTreeError('Access is restricted from your current location.', 403, true));
    } else if (isNoRoleBlock(ex)) {
      yield put(featureTreeError(
        'You are not authorized to access this application. Please contact your administrator to request access.',
        403,
        false,
        true
      ));
    } else {
      const status = ex instanceof FeatureTreeApiError ? ex.status : null;
      // Pass the backend's own message through when present, so an app-wide block whose wording
      // didn't match the geo/role patterns still shows a meaningful reason (not a blank spinner).
      const serverMessage =
        ex instanceof FeatureTreeApiError && ex.serverMessage ? ex.serverMessage : null;
      yield put(featureTreeError(serverMessage ?? 'Failed to resolve security feature tree.', status, false));
    }
  }
}
